import React, { useState } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import { Checkbox } from "evergreen-ui";
import { DatePicker, Form, Input, Button, Select, message } from "antd";
import { useListVals } from "../../../Firebase/databaseHooks";
import Firebase from "../../../Firebase/Firebase";
import { getDatabase, ref } from "firebase/database";
import { v4 as uuidv4 } from 'uuid';
import moment from "moment";
import { filterSelectOption, sortSelectOptions } from "../../../utils/selectOptions.js";

const { TextArea } = Input;
const longRegex = /^(-?(?:1[0-7]|[1-9])?\d(?:\.\d{1,8})?|180(?:\.0{1,8})?)$/;
const latRegex = /^(-?[1-8]?\d(?:\.\d{1,8})?|90(?:\.0{1,8})?)$/;
const db = getDatabase(Firebase);

const CommunitiesActionLayout = (props) => {
	const [form] = Form.useForm();
	const [communities] = useListVals(ref(db, "talmidsdb/communities")) || [];
	const [countries] = useListVals(ref(db, "talmidsdb/countries")) || [];
	const [userArray] = useListVals(ref(db, "talmidsdb/users")) || [];
	const [orgArray] = useListVals(ref(db, "talmidsdb/organisations")) || [];
	const [zones] = useListVals(ref(db, "talmidsdb/zones")) || [];
	const [districts] = useListVals(ref(db, "talmidsdb/district")) || [];
	const [coreChecked, setCoreChecked] = useState(false);
	const [defaultZone, setDefaultZone] = useState();
	const [defaultCountry, setDefaultCountry] = useState();

	const districtChange = (option) => {
		const district = districts?.find(x => x.districtID === option);
		if (!district) return;

		const districtProvince = district.province;
		const newZoneArray = zones?.filter(zone =>
			zone.zone && districtProvince &&
			String(districtProvince).replace(/r/g, '-').toLowerCase().includes(String(zone.zone).replace(/r/g, '-').toLowerCase()) ||
			String(zone.zone).replace(/r/g, '-').toLowerCase().includes(String(districtProvince).replace(/r/g, '-').toLowerCase()));

		newZoneArray?.length > 0 && setDefaultZone(newZoneArray[0].zoneID);

		const newCountryArray = countries?.filter(x => x.alpha3Code && districtProvince &&
			String(districtProvince).includes(x.alpha3Code));
		setDefaultCountry(newCountryArray?.[0]?.countryName + "-" + newCountryArray?.[0]?.alpha3Code);
	}

	const handleCreate = (values) => {
		const timeStamp = moment().format("YYYY-MM-DDTh:mm:ss");
		const objectID = uuidv4();

		if (communities.filter(x => x.name === values.name).length !== 0) {
			message.error('A community with the same name already exists! Enter a unique name');
		} else {
			const object = {
				"name": values.name || "",
				"id": objectID,
				"countryID": values.countryID || "",
				"startDate": values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
				"inactivationDate": values.inactivationDate ? values.inactivationDate.format("YYYY-MM-DD") : "",
				"orgID": values.orgID || "",
				"responsibleUserID": values.responsibleUserID || "",
				"districtID": values.districtID || "",
				"zoneID": values.zoneID || "",
				"advanced": values.advanced || false,
				"notes": values.notes || "",
				"salesforceID": values.salesforceID || "",
				"latitude": values.latitude || "",
				"longitude": values.longitude || "",
				"dateCreated": timeStamp
			}
			form.resetFields();
			props.createCallback("communities", objectID, object);
		}
	}

	const handleFailed = (err) => {
		message.error("Unable to create community: the form has errors: " + err.message);
	}

	return (
		<div className="font-Nunito">
			<MDBRow>
				<MDBCol className="px-4">
					<Form
						layout="vertical"
						onFinish={handleCreate}
						onFinishFailed={handleFailed}
						form={form}
						fields={[
							{
								name: ['country'],
								value: defaultCountry,
							},
							{
								name: ['zone'],
								value: defaultZone,
							}
						]}
					>
						<Form.Item
							label="Name"
							name="name"
							rules={[
								{ required: true, message: 'Please input community name!' },
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value) return Promise.resolve(); // Add this check
										if (communities?.filter(x =>
											x.name && x.countryID && // Add checks for these fields
											String(x.name).toLowerCase() === String(value).toLowerCase() &&
											String(x.countryID).toLowerCase() === String(getFieldValue('countryID')).toLowerCase()
										).length !== 0) {
											return Promise.reject('Community name and Country already exists!');
										}
										return Promise.resolve();
									},
								})
							]}
						>
							<Input
								type="text"
								placeholder="enter Community name"
								className="w-100 mr-4"
								id="email"
							/>
						</Form.Item>

						<Form.Item
							label="Country"
							name="countryID"
							rules={[{ required: true, message: 'Please input community country!' }]}
						>
							<Select
								placeholder="select Community's Country"
								name="countryID"
								showSearch
								value={defaultCountry}
								optionFilterProp="children"
								filterOption={filterSelectOption}
								filterSort={sortSelectOptions}
							>
								{countries.map((item, index) => (
									<Select.Option value={item.id} key={index}>
										{item.countryName}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							label="Zone"
							name="zoneID"
							rules={[{ required: true, message: 'Please input community zone!' }]}
						>
							<Select
								placeholder="select Community Zone"
								showSearch
								optionFilterProp="children"
								filterOption={filterSelectOption}
								filterSort={sortSelectOptions}
							>
								{zones?.map((item, index) => (
									<Select.Option key={index} value={item.id}>{item.zone}</Select.Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item label="District" name="districtID">
							<Select
								placeholder="Select District"
								showSearch
								optionFilterProp="children"
								filterOption={filterSelectOption}
								filterSort={sortSelectOptions}
								onChange={districtChange}
							>
								{districts.map((item, index) => (
									<Select.Option value={item.id} key={index}>
										{item.districtName + "-" + item.provinceID}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item
							label="Organization"
							name="orgID"
							rules={[{ required: true, message: 'Please input community organisation!' }]}
						>
							<Select
								placeholder="Select Organization"
								showSearch
								optionFilterProp="children"
								filterOption={filterSelectOption}
								filterSort={sortSelectOptions}
							>
								{orgArray.map((item, index) => (
									<Select.Option value={item.id} key={index}>{item.name}</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label="Responsible User" name="responsibleUserID">
							<Select
								placeholder="Select responsible User"
								showSearch
								optionFilterProp="children"
								filterOption={filterSelectOption}
								filterSort={sortSelectOptions}
							>
								{userArray?.map((item, index) => (
									<Select.Option value={item.id} key={index}>
										{item.firstname + " " + item.surname}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item
							label="Latitude"
							name="latitude"
							rules={[{
								pattern: new RegExp(latRegex),
								message: "Wrong latitude format!"
							}]}
						>
							<Input type="number" placeholder="enter latitude" id="lat" />
						</Form.Item>

						<Form.Item
							label="Longitude"
							name="longitude"
							rules={[{
								pattern: new RegExp(longRegex),
								message: "Wrong longitude format!"
							}]}
						>
							<Input type="number" placeholder="enter longitude" id="long" />
						</Form.Item>

						<Form.Item
							label="Start Date"
							name="startDate"
							rules={[{ required: true, message: 'Please input start date!' }]}
						>
							<DatePicker
								placeholder="select starting date"
								picker={"date"}
								className="w-100"
							/>
						</Form.Item>

						<Form.Item name="inactivationDate" label="Inactivation Date">
							<DatePicker
								placeholder="select inactivation date"
								picker={"date"}
								className="w-100"
							/>
						</Form.Item>

						<Form.Item
							label="Advanced"
							name="advanced"
							valuePropName="checked"
						>
							<Checkbox
								label="Advanced"
								checked={coreChecked}
								onChange={e => setCoreChecked(e.target.checked)}
							/>
						</Form.Item>

						<Form.Item label="Notes" name="notes">
							<TextArea
								type="text"
								placeholder="enter notes"
								className="w-100 mr-4"
								id="notes"
							/>
						</Form.Item>

						<Form.Item label="Salesforce ID" name="salesforceID">
							<Input
								type="text"
								placeholder="enter Community SalesForce ID"
								className="w-100 mr-4"
								id="salesforce"
							/>
						</Form.Item>

						<Button type="primary" htmlType="submit">
							Create
						</Button>
					</Form>
				</MDBCol>
			</MDBRow>
		</div>
	);
}

export default CommunitiesActionLayout;